import * as R from 'ramda';
import { createSelector } from 'reselect';

import { Customer } from 'types/Customer';
import { PosPlugin } from 'plugins/plugin';
import { getCustomers, getSalesDocuments } from 'services/ErplyAPI';
import {
  getLastProductIndex,
  getProductsInShoppingCart,
} from 'reducers/ShoppingCart';
import { ErplyAttributes } from 'utils';
import { getSetting } from 'reducers/configs/settings';
import { addWarning } from 'actions/Error';

import {
  TafCustomerSearchModal,
  TafCouponsPoints,
} from './components/components';
import {
  UIVisibleCustomerInformation,
  UICustomTableBillRow,
  UICustomerSearch,
  SearchCouponsModal,
  UIExtraActionButtons,
  UICoupons,
  UICustomerResults,
  UICustomerBadgeCustomRegion,
  UITableStats,
} from './components';
import tafReducer, {
  getIsMedicalEntitySelected,
  getIsSchoolEntitySelected,
  getTafReduxState,
} from './rdx/reducers';
import {
  resetTafState,
  removeProductInTafState,
  resetEditState,
  updateProductsState,
  setEditState,
  setGlobalEntity,
  setCustomerLastUsedEntities,
} from './rdx/actions';
import documentation from './documentation.md';

const TafPlugin: PosPlugin = {
  id: 'taf-plugin',
  name: 'TAF Configuration',
  keywords: ['taf', 'configuration'],
  infoMDUrl: documentation,
  getStatus: () => {
    return {
      type: 'valid',
      message: `Ready`,
    };
  },
  components: {
    TafCustomerSearchModal,
    TafCouponsPoints,
    SearchCouponsModal,
  },
  onDoCustomerSearch: {
    on: (p, ap) => async (dispatch, getState) => {
      const state = getState();
      const {
        applyTafLogic,
        selectedEntityType,
        isEditMode,
        editState,
      } = getTafReduxState(state);

      const { searchValue } = p;

      if (applyTafLogic) {
        const associationGroupID = getSetting(
          'touchpos_customer_association_group_id',
        )(state);

        const professionalGroupID = getSetting(
          'touchpos_customer_proffessional_group_id',
        )(state);

        const defineGroupIDValue = () => {
          const entityToConsider = isEditMode
            ? editState.newEntityType
            : selectedEntityType;

          return entityToConsider === 'association'
            ? associationGroupID
            : professionalGroupID;
        };

        const customerPromiseByID: Promise<Customer[]> = getCustomers({
          searchName: searchValue,
          recordsOnPage: 100,
          getAssociationsAndProfessionals: 1,
          groupIDWithSubgroups: defineGroupIDValue(),
        });

        if (ap.promises) {
          return {
            ...ap,
            promises: [customerPromiseByID],
          };
        }
      }

      const isID = /^\d+$/.test(String(searchValue));

      if (isID) {
        const customerPromiseByID: Promise<Customer[]> = getCustomers({
          customerID: searchValue,
        });

        if (ap.promises) {
          ap.promises.push(customerPromiseByID);
        }
      }

      return ap;
    },
  },
  reduxReducer: tafReducer,
  UIVisibleCustomerInformation,
  UICustomTableBillRow,
  UICustomerSearch,
  UIExtraActionButtons,
  UICoupons,
  UICustomerResults,
  UICustomerBadgeCustomRegion,
  UITableStats,
  onResetShoppingCart: {
    after: () => async dispatch => {
      dispatch(resetTafState());
    },
  },
  onStartNewSale: {
    after: () => async dispatch => {
      dispatch(resetTafState());
    },
  },
  getTranslationOverrides: createSelector(
    () => null,
    () => ({
      gridButtons: {
        saleOptions: {
          coupons: 'Check MF Voucher',
          saveAsLayaway: 'Save As Layby Payment',
        },
        functions: {
          layawaySales: 'Laybys',
          pendingSales: 'Fit Pad Sales',
          coupons: 'Check MF Voucher',
        },
      },
    }),
  ),
  onAddProduct: {
    after: () => async (dispatch, getState) => {
      const productIndex = getLastProductIndex(getState());
      const isSchoolSelected = getIsSchoolEntitySelected(getState());
      const isMedicalSelected = getIsMedicalEntitySelected(getState());

      if (isMedicalSelected || isSchoolSelected) {
        dispatch(updateProductsState([productIndex]));
      }
    },
  },
  onRemoveOrder: {
    after: ({ orderIndex }) => async dispatch => {
      dispatch(removeProductInTafState(Number(orderIndex)));
    },
  },
  onSaveSalesDocument: {
    on: (p, requests) => async (dispatch, getState) => {
      const { productsState, fitID } = getTafReduxState(getState());

      const saleDoc = requests.find(
        request => request.requestName === 'saveSalesDocument',
      );

      Object.entries(productsState).forEach(([productIndex, product]) => {
        const schoolID = product.schoolEntity?.entityID;
        const medicalID = product.medicalEntity?.entityID;

        if (schoolID && saleDoc) {
          saleDoc[`associateID${productIndex}`] = schoolID;
        }

        if (medicalID && saleDoc) {
          saleDoc[`professionalID${productIndex}`] = medicalID;
        }
      });

      return requests.map(request => {
        if (request.requestName === 'saveSalesDocument' && fitID.length) {
          return {
            ...ErplyAttributes.withoutFlatArray(request),
            ...ErplyAttributes.fromFlatArray(request).set('fit_id', fitID)
              .asFlatArray,
          };
        }

        return request;
      });
    },
    after: () => async dispatch => {
      dispatch(resetTafState());
    },
  },
  onSetCustomer: {
    before: p => async (dispatch, getState) => {
      const state = getState();
      const {
        isEditMode,
        editState,
        applyTafLogic,
        selectedEntityType,
      } = getTafReduxState(state);
      const products = getProductsInShoppingCart(state);
      const { data, companyName } = p;

      if (isEditMode) {
        await dispatch(
          setEditState({
            newEntityName: companyName || ' ',
            newEntityID: data,
          }),
        );
        const { selectedOrderIndex } = editState;

        dispatch(updateProductsState([Number(selectedOrderIndex)]));
        dispatch(resetEditState());

        // Thrown Error to avoid adding the customer
        throw new Error(
          'Entity selected to be assigned to the sale, stopping entity to be added as selected customer',
        );
      }

      if (applyTafLogic) {
        await dispatch(
          setGlobalEntity({
            entityType: selectedEntityType,
            entityID: data,
            entityName: companyName || ' ',
          }),
        );

        dispatch(
          updateProductsState(products.map(({ orderIndex }) => orderIndex)),
        );

        // Thrown Error to avoid adding the customer
        throw new Error(
          'Entity selected to be assigned to the sale, stopping entity to be added as selected customer',
        );
      }
    },
    after: (props, customerData) => async dispatch => {
      const documents = await getSalesDocuments({
        clientID: customerData.id,
        getRowsForAllInvoices: 1,
      });

      const allRows = documents.flatMap(doc => doc.rows);
      const assocIds = allRows
        .map(row => row.associationID)
        .filter(v => v !== undefined && v !== 0);
      const uniqueAssocIds = [...new Set(assocIds)].slice(0, 4);

      const proffIds = allRows
        .map(row => row.professionalID)
        .filter(v => v !== undefined && v !== 0);
      const uniqueProffIds = [...new Set(proffIds)].slice(0, 4);

      try {
        const [prevAssocObjects, prevProffObjects]: [
          Customer[],
          Customer[],
        ] = await Promise.all([
          getCustomers({ customerIDs: uniqueAssocIds.join(',') }),
          getCustomers({ customerIDs: uniqueProffIds.join(',') }),
        ]);

        dispatch(
          setCustomerLastUsedEntities({
            previousAssociations: prevAssocObjects,
            previousProffesionals: prevProffObjects,
          }),
        );
      } catch (error) {
        dispatch(
          addWarning(
            'There was an error while retrieving previously selected entities',
            {
              dismissible: false,
            },
          ),
        );
      }
    },
  },
  selectorOverrides: {
    getSettings: base =>
      createSelector(
        base,
        R.assoc('pos_brazil_show_current_document_type_above_cart', true),
      ),
  },
};

export default TafPlugin;
